/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
import { HederaId } from '../../domain/context/shared/HederaId.js';
import { PublicKey as HPublicKey, } from '@hashgraph/sdk';
import LogService from '../../app/service/LogService.js';
import PublicKey from '../../domain/context/account/PublicKey.js';
import { FactoryKey } from '../../domain/context/factory/FactoryKey.js';
export default class TransactionAdapter {
    transfers(coin, amounts, targetsId, targetId) {
        throw new Error('Method not implemented.');
    }
    init() {
        throw new Error('Method not implemented.');
    }
    create(coin, factory, hederaTokenManager, createReserve, reserveAddress, reserveInitialAmount, proxyAdminOwnerAccount) {
        throw new Error('Method not implemented.');
    }
    getAccount() {
        throw new Error('Method not implemented.');
    }
    register(input) {
        throw new Error('Method not implemented.');
    }
    stop() {
        throw new Error('Method not implemented.');
    }
    wipe(coin, targetId, amount, startDate) {
        throw new Error('Method not implemented.');
    }
    cashin(coin, targetId, amount, startDate) {
        throw new Error('Method not implemented.');
    }
    burn(coin, amount, startDate) {
        throw new Error('Method not implemented.');
    }
    freeze(coin, targetId, startDate) {
        throw new Error('Method not implemented.');
    }
    unfreeze(coin, targetId, startDate) {
        throw new Error('Method not implemented.');
    }
    pause(coin, startDate) {
        throw new Error('Method not implemented.');
    }
    unpause(coin, startDate) {
        throw new Error('Method not implemented.');
    }
    rescue(coin, amount, startDate) {
        throw new Error('Method not implemented.');
    }
    rescueHBAR(coin, amount, startDate) {
        throw new Error('Method not implemented.');
    }
    delete(coin, startDate) {
        throw new Error('Method not implemented.');
    }
    transfer(coin, amount, sourceId, targetId, isApproval = false) {
        throw new Error('Method not implemented.');
    }
    getReserveAddress(coin) {
        throw new Error('Method not implemented.');
    }
    updateReserveAddress(coin, reserveAddress) {
        throw new Error('Method not implemented.');
    }
    getReserveAmount(coin) {
        throw new Error('Method not implemented.');
    }
    updateReserveAmount(reserveAddress, amount) {
        throw new Error('Method not implemented.');
    }
    update(coin, name, symbol, autoRenewPeriod, expirationTime, kycKey, freezeKey, feeScheduleKey, pauseKey, wipeKey, metadata) {
        throw new Error('Method not implemented.');
    }
    grantRole(coin, targetId, role) {
        throw new Error('Method not implemented.');
    }
    revokeRole(coin, targetId, role) {
        throw new Error('Method not implemented.');
    }
    grantRoles(coin, targetsId, roles, amounts, startDate) {
        throw new Error('Method not implemented.');
    }
    revokeRoles(coin, targetsId, roles, startDate) {
        throw new Error('Method not implemented.');
    }
    hasRole(coin, targetId, role) {
        throw new Error('Method not implemented.');
    }
    grantSupplierRole(coin, targetId, amount) {
        throw new Error('Method not implemented.');
    }
    grantUnlimitedSupplierRole(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    revokeSupplierRole(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    balanceOf(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    associateToken(tokenId, targetId) {
        throw new Error('Method not implemented.');
    }
    isUnlimitedSupplierAllowance(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    supplierAllowance(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    resetSupplierAllowance(coin, targetId, startDate) {
        throw new Error('Method not implemented.');
    }
    increaseSupplierAllowance(coin, targetId, amount, startDate) {
        throw new Error('Method not implemented.');
    }
    decreaseSupplierAllowance(coin, targetId, amount, startDate) {
        throw new Error('Method not implemented.');
    }
    grantKyc(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    revokeKyc(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    async updateCustomFees(coin, customFees) {
        throw new Error('Method not implemented.');
    }
    getRoles(coin, targetId) {
        throw new Error('Method not implemented.');
    }
    getMirrorNodeAdapter() {
        throw new Error('Method not implemented.');
    }
    upgradeImplementation(proxy, proxyAdminId, implementationId) {
        throw new Error('Method not implemented.');
    }
    changeOwner(proxyAdminId, targetId) {
        throw new Error('Method not implemented.');
    }
    acceptOwner(proxyAdminId) {
        throw new Error('Method not implemented.');
    }
    async getEVMAddress(parameter) {
        if (parameter instanceof HederaId) {
            return (await this.getMirrorNodeAdapter().accountToEvmAddress(parameter)).toString();
        }
        return parameter;
    }
    setKeysForSmartContract(providedKeys) {
        const keys = [];
        providedKeys.forEach((providedKey, index) => {
            if (providedKey) {
                const key = new FactoryKey();
                switch (index) {
                    case 0: {
                        key.keyType = 1; // admin
                        break;
                    }
                    case 1: {
                        key.keyType = 2; // kyc
                        break;
                    }
                    case 2: {
                        key.keyType = 4; // freeze
                        break;
                    }
                    case 3: {
                        key.keyType = 8; // wipe
                        break;
                    }
                    case 4: {
                        key.keyType = 16; // supply
                        break;
                    }
                    case 5: {
                        key.keyType = 32; // fee schedule
                        break;
                    }
                    case 6: {
                        key.keyType = 64; // pause
                        break;
                    }
                }
                const providedKeyCasted = providedKey;
                key.publicKey =
                    providedKeyCasted.key == PublicKey.NULL.key
                        ? '0x'
                        : HPublicKey.fromString(providedKeyCasted.key).toBytesRaw();
                key.isED25519 = providedKeyCasted.type === 'ED25519';
                keys.push(key);
            }
        });
        return keys;
    }
    logTransaction(id, network) {
        const HASHSCAN_URL = `https://hashscan.io/${network}/transactionsById/`;
        const HASHSCAN_TX_URL = `https://hashscan.io/${network}/tx/`;
        const msg = `\nYou can see your transaction at ${id.startsWith('0x') ? HASHSCAN_TX_URL : HASHSCAN_URL}${id}\n`;
        LogService.logInfo(msg);
        console.log(msg);
    }
    sign(message) {
        throw new Error('Method not implemented.');
    }
    submit(t) {
        throw new Error('Method not implemented.');
    }
}
